// File: src/components/DocumentTemplates/AOA.js
import React, { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import DocumentLayout from "../DocumentLayout";

const AOA = () => {
    const [companyName, setCompanyName] = useState("Company Name Limited");
    const [shareClasses, setShareClasses] = useState([
        { class: "Ordinary Shares", rights: "Voting rights, dividend rights" },
        {
            class: "Preference Shares",
            rights: "Fixed dividend, priority in liquidation",
        },
    ]);
    const [directorPowers, setDirectorPowers] = useState(
        "The directors have the power to manage the business of the company"
    );
    const [meetingRules, setMeetingRules] = useState(
        "Annual general meetings must be held within 6 months of financial year end"
    );
    const [selectedTemplate, setSelectedTemplate] = useState("1");

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `${companyName}-AOA`,
    });

    const addShareClass = () =>
        setShareClasses([...shareClasses, { class: "", rights: "" }]);
    const updateShareClass = (i, field, val) => {
        const newClasses = [...shareClasses];
        newClasses[i][field] = val;
        setShareClasses(newClasses);
    };
    const removeShareClass = (i) =>
        setShareClasses(shareClasses.filter((_, idx) => idx !== i));

    const renderTemplate = () => {
        switch (selectedTemplate) {
            case "1": // Classic Legal
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-10 border-4 border-purple-900 shadow-xl max-w-3xl mx-auto"
                    >
                        <h2 className="text-2xl font-bold text-center mb-2 underline">
                            ARTICLES OF ASSOCIATION
                        </h2>
                        <p className="text-center text-gray-700 mb-6">OF</p>
                        <h1 className="text-3xl font-extrabold text-purple-800 text-center mb-8 uppercase">
                            {companyName}
                        </h1>

                        <h3 className="font-semibold mb-2">Share Classes:</h3>
                        <ul className="list-disc list-inside space-y-2 mb-4">
                            {shareClasses.map((s, i) => (
                                <li key={i}>
                                    <b>{s.class}</b>: {s.rights}
                                </li>
                            ))}
                        </ul>

                        <p className="mb-4">
                            <b>Director Powers:</b> {directorPowers}
                        </p>
                        <p className="mb-4">
                            <b>Meeting Rules:</b> {meetingRules}
                        </p>

                        <div className="mt-12 flex justify-between">
                            <div>
                                <p className="text-sm">_________________________</p>
                                <p className="text-sm">Director</p>
                            </div>
                            <p className="text-sm">Dated: {new Date().toDateString()}</p>
                        </div>
                    </div>
                );

            case "2": // Modern Clean
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-10 shadow-lg rounded-lg border max-w-3xl mx-auto"
                    >
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold tracking-wide text-purple-900">
                                Articles of Association
                            </h2>
                            <p className="text-sm text-gray-600">Incorporated under Company Law</p>
                        </div>

                        <h1 className="text-3xl font-bold text-purple-700 text-center mb-6">
                            {companyName}
                        </h1>

                        <div className="mb-6">
                            <h3 className="font-semibold text-lg mb-2">Share Classes</h3>
                            {shareClasses.map((s, i) => (
                                <p key={i}>
                                    <b>{s.class}</b> – {s.rights}
                                </p>
                            ))}
                        </div>

                        <p className="mb-4">
                            <b>Director Powers:</b> {directorPowers}
                        </p>
                        <p className="mb-4">
                            <b>Meeting Rules:</b> {meetingRules}
                        </p>

                        <div className="border-t pt-6 flex justify-between items-center">
                            <div>
                                <p className="text-sm">Authorized Signatory</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs">Issued: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                );

            case "3": // Decorative Header
                return (
                    <div
                        ref={componentRef}
                        className="bg-white p-8 shadow-lg border max-w-3xl mx-auto relative overflow-hidden"
                    >
                        <div className="bg-gradient-to-r from-purple-700 to-indigo-600 text-white p-6 rounded-t">
                            <h2 className="text-2xl font-bold text-center">
                                ARTICLES OF ASSOCIATION
                            </h2>
                        </div>

                        <div className="p-6">
                            <h1 className="text-3xl font-bold text-center text-purple-700 mb-6">
                                {companyName}
                            </h1>

                            <h3 className="font-semibold mb-2">Share Classes</h3>
                            <ul className="list-disc list-inside mb-4">
                                {shareClasses.map((s, i) => (
                                    <li key={i}>
                                        <b>{s.class}</b> – {s.rights}
                                    </li>
                                ))}
                            </ul>

                            <p className="mb-4">
                                <b>Director Powers:</b> {directorPowers}
                            </p>
                            <p className="mb-4">
                                <b>Meeting Rules:</b> {meetingRules}
                            </p>

                            <div className="mt-12 flex justify-between">
                                <div>
                                    <p className="text-sm">_________________________</p>
                                    <p className="text-xs">Director</p>
                                </div>
                                <p className="text-xs">Issued: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (

        <div className="space-y-6 bg-slate-50 p-6">
            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium">Company Name</label>
                    <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium">Template Style</label>
                    <select
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                        className="w-full p-2 border rounded"
                    >
                        <option value="1">Classic Legal</option>
                        <option value="2">Modern Clean</option>
                        <option value="3">Decorative Header</option>
                    </select>
                </div>
            </div>

            {/* Share Classes */}
            <div>
                <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Share Classes</label>
                    <button
                        onClick={addShareClass}
                        className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                    >
                        Add Class
                    </button>
                </div>
                <div className="space-y-4">
                    {shareClasses.map((s, i) => (
                        <div
                            key={i}
                            className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                        >
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="font-medium text-gray-700">
                                    Share Class {i + 1}
                                </h4>
                                <button
                                    onClick={() => removeShareClass(i)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">
                                        Class Name
                                    </label>
                                    <input
                                        type="text"
                                        value={s.class}
                                        onChange={(e) =>
                                            updateShareClass(i, "class", e.target.value)
                                        }
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">
                                        Rights
                                    </label>
                                    <input
                                        type="text"
                                        value={s.rights}
                                        onChange={(e) =>
                                            updateShareClass(i, "rights", e.target.value)
                                        }
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Director Powers */}
            <div>
                <label className="block text-sm font-medium">Director Powers</label>
                <textarea
                    value={directorPowers}
                    onChange={(e) => setDirectorPowers(e.target.value)}
                    rows="3"
                    className="w-full p-2 border rounded"
                />
            </div>

            {/* Meeting Rules */}
            <div>
                <label className="block text-sm font-medium">Meeting Rules</label>
                <textarea
                    value={meetingRules}
                    onChange={(e) => setMeetingRules(e.target.value)}
                    rows="3"
                    className="w-full p-2 border rounded"
                />
            </div>
            <DocumentLayout
                title="Articles of Association"
                description="Company articles of association document"
                onSave={() => alert("AOA saved!")}
                onDownload={handlePrint}
                onPrint={handlePrint}
            >
                {/* Preview */}
                <div className="mt-8 border rounded bg-gray-50">{renderTemplate()}</div>
            </DocumentLayout>
        </div>
    );
};

export default AOA;
